// DetailLaporan.js (React JS)
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Icon,
} from "@mui/material";
import { PictureAsPdf } from "@mui/icons-material";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/id";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { API_URL, API_URL_PDF } from "../api";
import { CustomModal } from "../components/CustomModal";


export default function DetailLaporan() {
  const { reportType, reportId, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Determine report type from path if not in params
  const getReportTypeFromPath = () => {
    if (location.pathname.includes('/detail-inspeksi/')) return 'inspection';
    if (location.pathname.includes('/detail-observasi/')) return 'observation';
    return reportType || 'harian';
  };

  const effectiveReportType = getReportTypeFromPath();
  const effectiveReportId = reportId || id;
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [userJabatan, setUserJabatan] = useState('');
  const [modalConfig, setModalConfig] = useState({
    title: "",
    message: "",
    type: "info",
    buttons: [],
  });

  const showModal = (title, message, type = "info", buttons = []) => {
    setModalConfig({ title, message, type, buttons });
    setModalVisible(true);
  };

  const ALLOWED_JABTAN = [
    'Superintendent GIS',
    'Superintendent Pit Utara',
    'Superintendent Pit Selatan',
    'Supervisor Pit Selatan',
    'Supervisor Pit Utara',
  ]

  const canFollowUp = ALLOWED_JABTAN.some(
    j => j.toLowerCase() === userJabatan.toLowerCase()
  )

  useEffect(() => {

    if (!effectiveReportId) {
      showModal(
        "Kesalahan",
        "reportId tidak ditemukan di URL. Kembali ke daftar.",
        "error",
        [{ text: "OK", onPress: () => navigate("/") }]
      );
      setLoading(false);
      return;
    }
    fetchReportDetails();
  }, [effectiveReportId, effectiveReportType]);

  useEffect(() => {
    fetchReportDetails();
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        if (userData.jabatan) {
          setUserJabatan(userData.jabatan);
        }
      } catch (error) {
        console.error("Gagal membaca data user:", error);
      }
    }
  }, []);

  const fetchReportDetails = async () => {
    setLoading(true);
    try {
      let endpoint;
      switch (effectiveReportType) {
        case "p5m":
          endpoint = `${API_URL}/p5m/reports/${effectiveReportId}`;
          break;
        case "kerusakan":
          endpoint = `${API_URL}/alat/reportskerusakan/${effectiveReportId}`;
          break;
        case "checklist":
          endpoint = `${API_URL}/drone-checklists/${effectiveReportId}`;
          break;
        case "inspection":
          endpoint = `${API_URL}/inspection-reports/${effectiveReportId}`;
          break;
        case "observation":
          endpoint = `${API_URL}/observation-reports/${effectiveReportId}`;
          break;
        case 'followup':
          endpoint = `${API_URL}/alat/followup/${reportId}`;
          break;
        default:
          endpoint = `${API_URL}/laporan-harian/${effectiveReportId}`;
      }

      const token = localStorage.getItem("userToken");
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (["p5m", "kerusakan", "checklist", "inspection", "observation"].includes(effectiveReportType)) {
        if (response.data.success) setReport(response.data.data);
        else throw new Error(response.data.error || "Data tidak valid");
      } else {
        setReport(response.data);
      }
    } catch (error) {
      console.error("Error fetching report details:", error);
      showModal("Gagal", "Gagal memuat detail laporan", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChecklistPDF = async () => {
    const url = `${API_URL}/drone-checklists/${reportId}/pdf`;
    setIsDownloading(true);

    try {
      const response = await axios.get(url, {
        responseType: "blob",
      });

      const contentDisposition = response.headers["content-disposition"];

      // Buat nama file default menggunakan tanggal dari laporan.
      const dateString = dayjs(report.created_at || report.tanggal).format(
        "YYYY-MM-DD"
      );
      let fileName = `${dateString}-checklist-drone.pdf`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch && fileNameMatch.length === 2) {
          fileName = fileNameMatch[1];
        }
      }

      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading checklist PDF:", error);
      if (!error.response) {
        showModal("Gagal", "Jaringan terputus, gagal mengunduh file.", "error");
      } else {
        showModal("Gagal", "Gagal mengunduh file PDF checklist.", "error");
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadPDF = async () => {
    if (!report?.file_path) {
      showModal("Gagal", "File PDF tidak tersedia", "error");
      return;
    }
    setIsDownloading(true);
    const fileUrl = `${API_URL_PDF}/public/${report.file_path}`;
    const fileName = report.file_path.split("/").pop();

    try {
      const response = await axios.get(fileUrl, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      if (!error.response) {
        showModal("Gagal", "Jaringan terputus, gagal mengunduh file.", "error");
      } else {
        showModal("Gagal", "Gagal mengunduh file PDF.", "error");
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const getReportTitle = () => {
    switch (reportType) {
      case "p5m":
        return "Detail Laporan P5M";
      case "kerusakan":
        return "Detail Laporan Kerusakan";
      case "checklist":
        return "Detail Checklist Drone";
      case "inspection":
        return "Detail Inspeksi";
      case "observation":
        return "Detail Observasi";
      default:
        return "Detail Laporan Harian";
    }
  };

  if (loading)
    return (
      <Box sx={{ textAlign: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  if (!report)
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Data laporan tidak ditemukan</Typography>
      </Box>
    );

  return (
    <div className="form-container">
      <header className="app-header">
        <div className="header-content">
          <button
            className="back-button"
            onClick={() => navigate("/menu-riwayat-laporan")}
          >
            <Icon>arrow_back</Icon>
          </button>
          <h1 className="header-title"> Detail Laporan</h1>
          <div className="header-spacer"></div>
        </div>
      </header>
      <Box sx={{ mx: "auto", p: 2 }}>
        <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Tanggal:</strong>{" "}
            {dayjs(report.created_at || report.tanggal)
              .locale("id")
              .format("dddd, DD MMMM YYYY")}
          </Typography>

          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Lokasi:</strong> {report.lokasi}
          </Typography>

          {reportType === "checklist" && (
            <>
              {report.pilot && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Nama Pilot:</strong> {report.pilot}
                </Typography>
              )}
              {report.cuaca && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Cuaca:</strong> {report.cuaca}
                </Typography>
              )}
              {report.kondisi_pilot && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Kondisi Pilot:</strong> {report.kondisi_pilot}
                </Typography>
              )}
              {report.status && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Status Persetujuan:</strong> {report.status}
                </Typography>
              )}
              {report.status === "Disetujui" && (
                <Button
                  variant="contained"
                  color="error"
                  fullWidth
                  disabled={isDownloading}
                  startIcon={!isDownloading && <PictureAsPdf />}
                  onClick={handleChecklistPDF}
                  sx={{
                    mt: 2,
                    textTransform: "none",
                    fontWeight: "bold",
                    borderRadius: "8px",
                    padding: "8px 22px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    "&:hover": {
                      boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.2s ease-out",
                  }}
                >
                  {isDownloading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Download PDF"
                  )}
                </Button>
              )}
            </>
          )}

          {reportType === "harian" && report.nama && (
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Nama:</strong> {report.nama}
            </Typography>
          )}

          {reportType === "observation" && (
            <>
              {report.kegiatan && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Kegiatan:</strong> {report.kegiatan}
                </Typography>
              )}
              {report.observer_name && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Observer:</strong> {report.observer_name}
                </Typography>
              )}
              {report.observesed_person_name && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Person Observed:</strong> {report.observesed_person_name}
                </Typography>
              )}
            </>
          )}

          {reportType === "inspection" && (
            <>
              {report.inspectors && Array.isArray(report.inspectors) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" fontWeight="bold" gutterBottom>
                    Inspectors:
                  </Typography>
                  {report.inspectors.map((insp, idx) => (
                    <Typography key={idx} variant="body2" sx={{ ml: 2 }}>
                      - {insp.nama} ({insp.jabatan})
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}

          {reportType === "kerusakan" && (
            <>
              {report.nama_alat && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Nama Alat:</strong> {report.nama_alat}
                </Typography>
              )}
              {report.serial_number && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Serial Number:</strong> {report.serial_number}
                </Typography>
              )}
              {report.nama_pengguna && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Nama Pengguna:</strong> {report.nama_pengguna}
                </Typography>
              )}
              {report.keterangan_kerusakan && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Keterangan:</strong> {report.keterangan_kerusakan}
                </Typography>
              )}
              {report.status && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Status:</strong>{" "}
                  <span
                    style={{
                      color:
                        report.status?.toLowerCase() === "selesai"
                          ? "#27ae60"
                          : "#e67e22",
                      fontWeight: "bold",
                    }}
                  >
                    {report.status}
                  </span>
                </Typography>
              )}

              {/* ── Info Followup ─────────────────────────────────── */}
              {(report.penerima_laporan ||
                report.tanggal_terima ||
                report.tanggal_mulai ||
                report.tanggal_selesai) && (
                  <Box
                    sx={{
                      backgroundColor: "#f0fdf4",
                      borderRadius: "10px",
                      border: "1px solid #bbf7d0",
                      p: 2,
                      mt: 2,
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        color: "#166534",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        mb: 1.5,
                      }}
                    >
                      📌 Info Follow Up
                    </Typography>

                    {report.penerima_laporan && (
                      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1.5 }}>
                        <Typography sx={{ fontSize: 16, mr: 1.5, mt: 0.5 }}>👤</Typography>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 0.5 }}>Diterima Oleh</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
                            {report.penerima_laporan}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {report.tanggal_terima && (
                      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1.5 }}>
                        <Typography sx={{ fontSize: 16, mr: 1.5, mt: 0.5 }}>📥</Typography>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 0.5 }}>Diterima Oleh:</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#111827", mb: 0.5 }}>
                            {report.nama_penerima}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 0.5 }}>Tanggal Diterima</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
                            {dayjs(report.tanggal_terima).locale("id").format("dddd, DD MMMM YYYY HH:mm")}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {report.tanggal_mulai && (
                      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1.5 }}>
                        <Typography sx={{ fontSize: 16, mr: 1.5, mt: 0.5 }}>🔧</Typography>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 0.5 }}>Dikirim Oleh:</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#111827", mb: 0.5 }}>
                            {report.nama_maintenance}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 0.5 }}>Tanggal Mulai Maintenance</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
                            {dayjs(report.tanggal_mulai).locale("id").format("dddd, DD MMMM YYYY HH:mm")}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {report.tanggal_selesai && (
                      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        <Typography sx={{ fontSize: 16, mr: 1.5, mt: 0.5 }}>✅</Typography>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 0.5 }}>Dilaporkan Oleh:</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#111827", mb: 0.5 }}>
                            {report.nama_selesai}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 0.5 }}>Tanggal Selesai</Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#27ae60" }}>
                            {dayjs(report.tanggal_selesai).locale("id").format("dddd, DD MMMM YYYY HH:mm")}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
            </>
          )}

          {/* Tombol Follow Up — hanya untuk jabatan yang diizinkan */}
          {reportType === "kerusakan" &&
            report.status?.toLowerCase() !== "selesai" &&
            (canFollowUp ? (
              <Button
                variant="contained"
                fullWidth
                onClick={() =>
                  navigate(`/form-followup-kerusakan/${effectiveReportId}`, {
                    state: {
                      currentStatus: report.status?.toLowerCase() || "menunggu",
                    },
                  })
                }
                startIcon={<Icon>check_circle</Icon>}
                sx={{
                  mt: 2,
                  mb: 1,
                  backgroundColor:
                    report.status?.toLowerCase() === "menunggu" || !report.status
                      ? "#27ae60"
                      : report.status?.toLowerCase() === "diterima"
                        ? "#e67e22"
                        : "#2980b9",
                  "&:hover": {
                    backgroundColor:
                      report.status?.toLowerCase() === "menunggu" || !report.status
                        ? "#219653"
                        : report.status?.toLowerCase() === "diterima"
                          ? "#d35400"
                          : "#2471a3",
                  },
                  textTransform: "none",
                  fontWeight: "bold",
                  borderRadius: "8px",
                  padding: "10px 22px",
                }}
              >
                {report.status?.toLowerCase() === "menunggu" || !report.status
                  ? "Terima Laporan"
                  : report.status?.toLowerCase() === "diterima"
                    ? "Mulai Maintenance"
                    : "Tandai Selesai"}
              </Button>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mt: 2,
                  py: 1.5,
                  px: 2,
                  backgroundColor: "#f3f4f6",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                }}
              >
                <Icon sx={{ fontSize: 18, color: "#9ca3af", mr: 1 }}>lock</Icon>
                <Typography sx={{ fontSize: 12, color: "#9ca3af", flex: 1 }}>
                  Hanya Superintendent atau Supervisor yang dapat memproses laporan ini
                </Typography>
              </Box>
            ))}

          {(report.keterangan || reportType === "p5m") && (
            <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
              <strong>Keterangan:</strong>{" "}
              {report.keterangan ||
                (reportType === "p5m" ? "Laporan P5M" : "Tidak ada keterangan")}
            </Typography>
          )}

          {report.file_path && (
            <Button
              variant="contained"
              fullWidth
              disabled={isDownloading}
              color="error"
              onClick={downloadPDF}
              startIcon={!isDownloading && <PictureAsPdf />}
              sx={{
                mt: 2,
                textTransform: "none",
                fontWeight: "bold",
                borderRadius: "8px",
                padding: "8px 22px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease-out",
              }}
            >
              {isDownloading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Download PDF"
              )}
            </Button>
          )}
        </Paper>

        <CustomModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          title={modalConfig.title}
          message={modalConfig.message}
          type={modalConfig.type}
          buttons={modalConfig.buttons}
        />
      </Box>
    </div>
  );
}
